import { useQuery } from '@tanstack/react-query';
import { BookedBenefitsEnum, PricingModelEnum } from '@pxtra-client-lib-legacy/gen';
import { apiClient } from '../../../lib/util';
import { checkHasCompanyBenefitSets } from '../../../lib/util/pricingModel';

export const useListBenefitSet = (
  companyId: string,
  pricingModel?: PricingModelEnum,
  bookedBenefits?: BookedBenefitsEnum[] | null,
  options = {}
) => {
  return useQuery({
    queryKey: ['benefitSets', companyId],
    queryFn: () => {
      const res = apiClient.benefits.benefitsBenefitSetsList({
        company: companyId
      });
      return res;
    },
    enabled: !!companyId && checkHasCompanyBenefitSets(pricingModel, bookedBenefits),
    staleTime: 2 * 1000,
    refetchOnWindowFocus: false,
    ...options
  });
};

export const useGetBenefitSet = (
  benefitSetId?: string,
  pricingModel?: PricingModelEnum,
  bookedBenefits?: BookedBenefitsEnum[] | null,
  options = {}
) => {
  return useQuery({
    queryKey: ['benefitSets', benefitSetId],
    queryFn: () => {
      if (!benefitSetId) {
        throw new Error('Benefit Set ID is required');
      }
      const res = apiClient.benefits.benefitsBenefitSetsRetrieve({
        id: benefitSetId
      });
      return res;
    },
    enabled: checkHasCompanyBenefitSets(pricingModel, bookedBenefits),
    ...options
  });
};

export const useGetBenefitSetStats = (companyId: string, options = {}) => {
  return useQuery({
    queryKey: ['benefitSetStats', companyId ?? 'MISSING_COMPANY_ID'],
    queryFn: () => {
      return apiClient.benefits.benefitsBenefitSetsStatsRetrieve({
        company: companyId
      });
    },
    enabled: !!companyId,
    ...options
  });
};

export const useGetUpcomingBenefitSets = (
  employeeId: string,
  pricingModel?: PricingModelEnum,
  bookedBenefits?: BookedBenefitsEnum[] | null
) => {
  return useQuery({
    queryKey: ['upcomingBenefitSets', employeeId],
    queryFn: async () => {
      const result = await apiClient.benefits.benefitsBenefitSetsUpcomingRetrieve({
        employee: employeeId
      });
      return result.data ?? [];
    },
    enabled: !!employeeId && checkHasCompanyBenefitSets(pricingModel, bookedBenefits),
    staleTime: 2 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
};

/**
 * Fetches the employee's benefit set for the next month (if no month and year specified) or for the specified month and year.
 *
 * @param employeeId - The ID of the employee
 * @param month - Optional month (0-indexed, will be incremented for accounting period)
 * @param year - Optional year for the accounting period
 * @param pricingModel - Optional pricing model to check for starter
 * @returns Query result containing the requested benefit set data
 */
export const useGetNextMonthBenefitSet = (
  employeeId: string,
  month?: number,
  year?: number,
  pricingModel?: PricingModelEnum,
  bookedBenefits?: BookedBenefitsEnum[] | null
) => {
  return useQuery({
    queryKey: ['nextMonthBenefitSet', employeeId, month, year],
    queryFn: async () => {
      const result = await apiClient.benefits.benefitsBenefitSetsNextMonthRetrieve({
        employee: employeeId,
        accountingPeriodMonth: month != null ? month + 1 : month,
        accountingPeriodYear: year
      });
      return result ?? {};
    },
    enabled: !!employeeId && checkHasCompanyBenefitSets(pricingModel, bookedBenefits),
    staleTime: 2 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
};
